import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { LaborComparison } from "@/components/LaborComparison";
import { UnclockedDeptList } from "@/components/UnclockedDeptList";
import { UnclockedReasonDialog, unclockedViolations } from "@/components/UnclockedReasonDialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Users2, Info, Download, BarChart3, CalendarClock, AlertTriangle, CheckCircle2, ChevronRight, Save, Wand2, Lock } from "lucide-react";

function fmtD(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function lastDayOfMonth(ym) {
  const [y, m] = ym.split("-").map(Number);
  return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
}

const COLS = [
  { key: "rooms_sold_prev_day", label: "Rooms Sold (Prev Day)" },
  { key: "employees", label: "# Employees" },
  { key: "total_hours", label: "Total Hours Worked" },
  { key: "rooms_cleaned", label: "Total Rooms Cleaned" },
  { key: "minutes_per_room", label: "Minutes / Room (auto)" },
  { key: "labor_cost", label: "Labor Cost (auto)", money: true },
];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function dayName(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" });
}
function money(v) {
  return `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PayrollAnalysis() {
  const [props, setProps] = useState([]);
  const { activePropertyId: propertyId, setActivePropertyId: setPropertyId } = useActiveProperty();
  const [month, setMonth] = useState(currentMonth());
  const [dates, setDates] = useState([]);
  const [rows, setRows] = useState({});
  const [byDept, setByDept] = useState([]);
  const [timesheet, setTimesheet] = useState([]);
  const [approval, setApproval] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportStart, setExportStart] = useState("");
  const [exportEnd, setExportEnd] = useState("");
  const [tab, setTab] = useState("analysis");
  const [issues, setIssues] = useState(null);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [openPeriods, setOpenPeriods] = useState({});
  const { user } = useAuth();
  const canFix = ["admin", "regional", "gm", "finance", "hr"].includes(user?.role);
  const [fix, setFix] = useState(null); // { start, employee_id, name }
  const [fixData, setFixData] = useState(null);
  const [fixHours, setFixHours] = useState({}); // { date: { position: value } }
  const [fixSaving, setFixSaving] = useState(false);
  const [fixViol, setFixViol] = useState(null);

  const fmtDShort = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "");

  const openFix = (period, exc) => {
    setFix({ start: period.start, end: period.end, employee_id: exc.employee_id, name: exc.name });
    setFixData(null); setFixHours({});
    api.get("/payroll-sheet/employee-days", { params: { property_id: propertyId, start: period.start, end: period.end, employee_id: exc.employee_id } })
      .then((r) => {
        setFixData(r.data);
        const init = {};
        (r.data.days || []).forEach((d) => {
          init[d.date] = {};
          (r.data.positions || []).forEach((pos) => { init[d.date][pos] = (d.saved?.[pos] ?? "") === "" ? "" : String(d.saved[pos]); });
        });
        setFixHours(init);
      })
      .catch((err) => { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load detail"); setFix(null); });
  };

  const autoBalanceAll = () => {
    if (!fixData) return;
    const next = {};
    fixData.days.forEach((d) => {
      const row = {};
      const byPos = d.clocked_by_position || {};
      const anyClocked = Object.keys(byPos).length > 0;
      fixData.positions.forEach((pos) => { row[pos] = byPos[pos] ? String(byPos[pos]) : ""; });
      if (!anyClocked && d.clocked_total > 0 && fixData.positions[0]) row[fixData.positions[0]] = String(d.clocked_total);
      next[d.date] = row;
    });
    setFixHours(next);
    toast.success("Balanced every day to its clocked hours — review & save");
  };

  const dayAllocated = (date) => fixData ? fixData.positions.reduce((s, pos) => s + (Number(fixHours[date]?.[pos]) || 0), 0) : 0;

  const saveFix = (overrideReasons = null) => {
    if (!fixData) return;
    setFixSaving(true);
    const days = fixData.days.map((d) => ({
      date: d.date,
      hours: Object.fromEntries(fixData.positions.map((pos) => [pos, Number(fixHours[d.date]?.[pos]) || 0])),
    }));
    api.post("/payroll-sheet/employee-days", { property_id: propertyId, start: fixData.start, employee_id: fixData.employee_id, days, override_reasons: overrideReasons || {} })
      .then(() => { toast.success("Corrections saved to Daily Payroll Entry"); setFixViol(null); setFix(null); setIssues(null); loadIssues(); setOrphans(null); })
      .catch((err) => {
        const viol = unclockedViolations(err);
        if (viol) setFixViol(viol);
        else toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
      })
      .finally(() => setFixSaving(false));
  };

  const loadIssues = useCallback(() => {
    if (!propertyId) return;
    setIssuesLoading(true);
    api.get("/payroll-sheet/issues", { params: { property_id: propertyId, count: 8 } })
      .then((r) => {
        const periods = r.data.periods || [];
        setIssues(periods);
        const focus = periods.find((p) => p.status === "submitted" && p.issue_count > 0)
          || periods.find((p) => p.issue_count > 0) || periods[0];
        setOpenPeriods(focus ? { [focus.start]: true } : {});
      })
      .catch(() => setIssues([]))
      .finally(() => setIssuesLoading(false));
  }, [propertyId]);
  useEffect(() => { setIssues(null); }, [propertyId]);
  const [orphans, setOrphans] = useState(null);
  const [orphanBusy, setOrphanBusy] = useState(null);
  const loadOrphans = useCallback(() => {
    api.get("/payroll-sheet/unclocked-departments", { params: { days: 120 } })
      .then((r) => setOrphans(r.data.items || []))
      .catch(() => setOrphans([]));
  }, []);
  useEffect(() => { if (tab === "issues" && orphans === null) loadOrphans(); }, [tab, orphans, loadOrphans]);
  const removeOrphan = (o) => {
    const key = `${o.property_id}|${o.employee_id}|${o.date}|${o.position}`;
    setOrphanBusy(key);
    api.post("/payroll-sheet/remove-saved-entry", { property_id: o.property_id, date: o.date, employee_id: o.employee_id, position: o.position })
      .then((r) => { toast.success(`Removed ${r.data.removed_hours} hrs of ${o.position} for ${o.name}`); loadOrphans(); setIssues(null); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove"))
      .finally(() => setOrphanBusy(null));
  };
  useEffect(() => { if (tab === "issues" && issues === null && propertyId) loadIssues(); }, [tab, issues, propertyId, loadIssues]);

  const openExport = () => {
    setExportStart(`${month}-01`);
    setExportEnd(lastDayOfMonth(month));
    setExportOpen(true);
  };

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data);
      if (!propertyId && r.data[0]) setPropertyId(r.data[0].id);
    });
  }, []);

  const loadData = useCallback(() => {
    if (!propertyId || !month) return;
    setLoading(true);
    Promise.all([
      api.get("/payroll", { params: { property_id: propertyId, month } }),
      api.get("/payroll/timesheet", { params: { property_id: propertyId, month } }),
    ])
      .then(([r, t]) => {
        setDates(r.data.dates);
        setRows(r.data.rows || {});
        setByDept(r.data.by_department || []);
        setApproval(r.data.approval || null);
        setTimesheet(t.data.employees || []);
      })
      .finally(() => setLoading(false));
  }, [propertyId, month]);

  useEffect(() => { loadData(); }, [loadData]);

  const fmt = (col, v) => {
    if (v == null || v === "" || (col.key !== "employees" && isNaN(Number(v)))) return "";
    if (col.money) return money(v);
    return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const totals = {
    total_hours: dates.reduce((s, d) => s + Number(rows[d]?.total_hours || 0), 0),
    rooms_cleaned: dates.reduce((s, d) => s + Number(rows[d]?.rooms_cleaned || 0), 0),
    labor_cost: dates.reduce((s, d) => s + Number(rows[d]?.labor_cost || 0), 0),
  };

  const exportXlsx = async () => {
    if (!exportStart || !exportEnd) return toast.error("Choose start and end dates");
    if (exportEnd < exportStart) return toast.error("End date must be on or after start date");
    setExporting(true);
    try {
      const res = await api.get("/payroll/export", {
        params: { property_id: propertyId, start_date: exportStart, end_date: exportEnd },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const name = (props.find((p) => p.id === propertyId)?.name || "Payroll").replace(/\s+/g, "_");
      a.href = url;
      a.download = `${name}_Payroll_${exportStart}_to_${exportEnd}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* noop */ }
      }
      toast.error(formatApiErrorDetail(detail));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Labor</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Payroll Analysis</h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Info className="h-3.5 w-3.5" /> Labor Cost = hours worked × rate of pay. Minutes/Room = (hours of room-cleaning staff × 60) ÷ Rooms Cleaned (only staff who cleaned rooms count).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="w-52" data-testid="payroll-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
            <SelectContent>{props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
          </Select>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="payroll-month-input" />
          <Button variant="outline" onClick={openExport} disabled={!propertyId || exporting} data-testid="payroll-export-btn">
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export Excel
          </Button>
        </div>
      </div>

      {propertyId && approval?.unapproved_days > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-2 border-red-500 bg-red-500/10 px-4 py-3 animate-pulse" data-testid="payroll-unapproved-banner">
          <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
          <div className="text-sm font-semibold text-red-700 dark:text-red-300">
            {approval.unapproved_days} {approval.unapproved_days > 1 ? "shifts of clocked hours are" : "shift of clocked hours is"} NOT approved yet.
            <span className="font-normal"> These hours won't post to payroll until a GM or Regional approves them in Time Clock.</span>
          </div>
          <a href="/time-clock" className="ml-auto shrink-0 rounded bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700" data-testid="payroll-goto-approve">Approve in Time Clock →</a>
        </div>
      )}
      {propertyId && approval?.has_punches && approval?.approved && (
        <div className="flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-400" data-testid="payroll-approved-banner">
          <CheckCircle2 className="h-4 w-4" /> All clocked hours for this month are approved.
        </div>
      )}

      {!propertyId ? (
        <div className="flex h-64 flex-col items-center justify-center border border-dashed border-border text-muted-foreground">
          <Users2 className="h-8 w-8" /><p className="mt-3 text-sm">Select a property to begin.</p>
        </div>
      ) : loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none">
            <TabsTrigger value="analysis" data-testid="tab-analysis"><BarChart3 className="mr-2 h-4 w-4" /> Daily Analysis</TabsTrigger>
            <TabsTrigger value="timesheet" data-testid="tab-timesheet"><CalendarClock className="mr-2 h-4 w-4" /> Timesheet History</TabsTrigger>
            <TabsTrigger value="issues" data-testid="tab-issues"><AlertTriangle className="mr-2 h-4 w-4" /> Issues</TabsTrigger>
          </TabsList>

          <TabsContent value="analysis" className="mt-4">
            <div className="relative overflow-auto border border-border bg-card" style={{ maxHeight: "70vh" }}>
              <table className="w-full border-collapse text-sm" data-testid="payroll-grid">
                <thead className="sticky top-0 z-20">
                  <tr>
                    <th className="border border-border bg-secondary px-3 py-2 text-left label-caps text-muted-foreground" style={{ minWidth: 60 }}>Date</th>
                    <th className="border border-border bg-secondary px-3 py-2 text-left label-caps text-muted-foreground" style={{ minWidth: 70 }}>Day</th>
                    {COLS.map((c) => (<th key={c.key} className="border border-border bg-secondary px-3 py-2 text-left text-xs font-semibold">{c.label}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {dates.map((d, ri) => (
                    <tr key={d} className={["Sat", "Sun"].includes(dayName(d)) ? "bg-accent/40" : ri % 2 ? "bg-muted/30" : ""}>
                      <td className="border border-border px-3 py-1.5 font-mono text-xs font-medium">{d.slice(5, 7)}/{d.slice(8, 10)}</td>
                      <td className="border border-border px-3 py-1.5 text-xs text-muted-foreground">{dayName(d)}</td>
                      {COLS.map((c) => (
                        <td key={c.key} className={`border border-border px-3 py-1.5 text-right font-mono text-xs ${c.key === "minutes_per_room" || c.key === "labor_cost" ? "bg-accent/30 font-semibold text-accent-foreground" : ""}`} data-testid={`pa-cell-${d}-${c.key}`}>
                          {fmt(c, rows[d]?.[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 z-10">
                  <tr className="bg-secondary font-semibold">
                    <td className="border border-border px-3 py-2 label-caps" colSpan={2}>Total</td>
                    <td className="border border-border" />
                    <td className="border border-border" />
                    <td className="border border-border px-3 py-2 text-right font-mono text-xs" data-testid="pa-total-hours">{totals.total_hours.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td className="border border-border px-3 py-2 text-right font-mono text-xs" data-testid="pa-total-rooms">{totals.rooms_cleaned.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td className="border border-border" />
                    <td className="border border-border px-3 py-2 text-right font-mono text-xs" data-testid="pa-total-labor">{money(totals.labor_cost)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {byDept.length > 0 && (
              <div className="mt-6" data-testid="by-department-section">
                <h3 className="mb-2 font-heading text-sm font-bold tracking-tight">By Department — {month} (includes auto salaried cost)</h3>
                <div className="overflow-auto border border-border bg-card">
                  <table className="w-full border-collapse text-sm" data-testid="by-department-table">
                    <thead>
                      <tr>
                        <th className="border border-border bg-secondary px-3 py-2 text-left text-xs font-semibold">Department</th>
                        <th className="border border-border bg-secondary px-3 py-2 text-right text-xs font-semibold">Total Hours</th>
                        <th className="border border-border bg-secondary px-3 py-2 text-right text-xs font-semibold">Total Payroll</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byDept.map((d) => (
                        <tr key={d.department} data-testid={`by-dept-row-${d.department}`}>
                          <td className="border border-border px-3 py-1.5 font-medium">{d.department}</td>
                          <td className="border border-border px-3 py-1.5 text-right font-mono text-xs">{Number(d.total_hours).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className="border border-border px-3 py-1.5 text-right font-mono text-xs">{money(d.total_payroll)}</td>
                        </tr>
                      ))}
                      <tr className="bg-secondary font-semibold">
                        <td className="border border-border px-3 py-2 label-caps">Total</td>
                        <td className="border border-border px-3 py-2 text-right font-mono text-xs" data-testid="by-dept-total-hours">{byDept.reduce((s, d) => s + Number(d.total_hours || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="border border-border px-3 py-2 text-right font-mono text-xs" data-testid="by-dept-total-payroll">{money(byDept.reduce((s, d) => s + Number(d.total_payroll || 0), 0))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="timesheet" className="mt-4">
            {timesheet.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center border border-dashed border-border text-muted-foreground">
                <CalendarClock className="h-8 w-8" /><p className="mt-3 text-sm">No timesheet entries logged this month.</p>
              </div>
            ) : (
              <div className="overflow-auto border border-border bg-card" data-testid="timesheet-table">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="border border-border bg-secondary px-3 py-2 text-left text-xs font-semibold">Employee</th>
                      <th className="border border-border bg-secondary px-3 py-2 text-left text-xs font-semibold">Department</th>
                      <th className="border border-border bg-secondary px-3 py-2 text-right text-xs font-semibold">Rate</th>
                      <th className="border border-border bg-secondary px-3 py-2 text-right text-xs font-semibold">Total Hours</th>
                      <th className="border border-border bg-secondary px-3 py-2 text-right text-xs font-semibold">Total Rooms</th>
                      <th className="border border-border bg-secondary px-3 py-2 text-right text-xs font-semibold">Labor Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timesheet.map((e) => (
                      <tr key={e.employee_id} data-testid={`timesheet-row-${e.employee_id}`}>
                        <td className="border border-border px-3 py-2 font-medium">{e.name}</td>
                        <td className="border border-border px-3 py-2 text-muted-foreground">{e.department}</td>
                        <td className="border border-border px-3 py-2 text-right font-mono text-xs">{money(e.rate_of_pay)}</td>
                        <td className="border border-border px-3 py-2 text-right font-mono text-xs">{e.total_hours.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="border border-border px-3 py-2 text-right font-mono text-xs">{e.total_rooms.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="border border-border px-3 py-2 text-right font-mono text-xs font-semibold text-accent-foreground">{money(e.labor_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="issues" className="mt-4">
            <div className="mb-3 rounded-none border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              Review payroll periods that were already run for this property. "Issues" are employees whose <span className="font-medium text-foreground">saved payroll hours don't match their approved Time Clock hours</span> — <span className="font-medium text-foreground">over</span> usually means hours were double-counted across positions, <span className="font-medium text-foreground">under</span> means a day is missing or wasn't entered. Fix on the Daily Report (Auto-balance) or Payroll Sheet (Recalculate from Time Clock). Salaried staff are excluded.
            </div>
            <UnclockedDeptList items={orphans} canFix={canFix} busy={orphanBusy} onRemove={removeOrphan} />
            {issuesLoading || issues === null ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : issues.length === 0 ? (
              <div className="flex h-40 flex-col items-center justify-center border border-dashed border-border text-muted-foreground">
                <CalendarClock className="h-8 w-8" /><p className="mt-3 text-sm">No pay periods found for this property.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="pa-issues-list">
                {issues.map((p) => {
                  const open = !!openPeriods[p.start];
                  return (
                    <div key={p.start} className="border border-border bg-card" data-testid={`pa-issue-period-${p.start}`}>
                      <button
                        onClick={() => setOpenPeriods((o) => ({ ...o, [p.start]: !o[p.start] }))}
                        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40"
                        data-testid={`pa-issue-toggle-${p.start}`}
                      >
                        <div className="flex flex-wrap items-center gap-3">
                          <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
                          <span className="font-semibold">{fmtD(p.start)} – {fmtD(p.end)}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide ${p.status === "submitted" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}>
                            {p.status === "submitted" ? "Run" : "Draft"}
                          </span>
                        </div>
                        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold ${p.issue_count > 0 ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`}>
                          {p.issue_count > 0 ? `${p.issue_count} issue${p.issue_count > 1 ? "s" : ""}` : "No issues"}
                        </span>
                      </button>
                      {open && (
                        <div className="border-t border-border">
                          {p.issue_count === 0 ? (
                            <p className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Saved payroll hours match the Time Clock for this period.</p>
                          ) : (
                            <>
                            {(p.pending_days || []).length > 0 && (
                              <p className="border-b border-border bg-sky-50 px-4 py-2 text-xs text-sky-800 dark:bg-sky-950/20 dark:text-sky-300" data-testid={`pa-pending-${p.start}`}>
                                <b>{p.pending_days.length} day{p.pending_days.length > 1 ? "s" : ""} not approved by GM</b> (clocked, but no Daily Report payroll saved): {p.pending_days.map((d) => new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })).join(", ")}
                              </p>
                            )}
                            {p.exceptions.length > 0 && (
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                                  <th className="px-4 py-2">Employee</th>
                                  <th className="px-4 py-2 text-right">Saved Hrs</th>
                                  <th className="px-4 py-2 text-right">Clocked Hrs</th>
                                  <th className="px-4 py-2 text-right">Difference</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.exceptions.map((x) => (
                                  <tr key={x.employee_id} className="border-t border-border" data-testid={`pa-issue-${p.start}-${x.employee_id}`}>
                                    <td className="px-4 py-2 font-medium">
                                      <button type="button" onClick={() => openFix(p, x)} className="text-left font-medium text-primary underline-offset-2 hover:underline" data-testid={`pa-issue-fix-${p.start}-${x.employee_id}`}>
                                        {x.name}
                                      </button>
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono">{x.saved_hours.toFixed(2)}</td>
                                    <td className="px-4 py-2 text-right font-mono text-muted-foreground">{x.clocked_hours.toFixed(2)}</td>
                                    <td className="px-4 py-2 text-right">
                                      <span className={`rounded-sm px-1.5 py-0.5 font-mono text-xs font-bold ${x.kind === "over" ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400" : "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400"}`}>
                                        {x.diff > 0 ? "+" : ""}{x.diff.toFixed(2)} {x.kind}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Export Payroll to Excel</DialogTitle>
            <DialogDescription>Choose the start and end dates for the report. Salaried staff are auto-costed per day (annual ÷ days in year).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Start Date</Label>
              <Input type="date" value={exportStart} onChange={(e) => setExportStart(e.target.value)} data-testid="export-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label>End Date</Label>
              <Input type="date" value={exportEnd} onChange={(e) => setExportEnd(e.target.value)} data-testid="export-end-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)}>Cancel</Button>
            <Button onClick={exportXlsx} disabled={exporting} data-testid="export-confirm-btn">
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LaborComparison propertyId={propertyId} />

      {/* Fix issue dialog: day-by-day re-allocation written to Daily Payroll Entry */}
      <UnclockedReasonDialog violations={fixViol} busy={fixSaving} onCancel={() => setFixViol(null)} onConfirm={(r) => saveFix(r)} />
      <Dialog open={!!fix} onOpenChange={(o) => !o && setFix(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="pa-fix-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Fix hours — {fix?.name}</DialogTitle>
            <DialogDescription>
              {fixData ? `${fmtD(fixData.start)} – ${fmtD(fixData.end)}` : ""} · set each day's hours so they match the Time Clock. Saving writes to the Daily Payroll Entry; punches are not changed.
            </DialogDescription>
          </DialogHeader>

          {!fixData ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : fixData.status === "submitted" ? (
            <div className="flex flex-col items-center justify-center gap-2 border border-dashed border-amber-400/60 bg-amber-50 p-6 text-center text-sm dark:bg-amber-950/20" data-testid="pa-fix-locked">
              <Lock className="h-6 w-6 text-amber-600" />
              <p className="font-semibold text-amber-700 dark:text-amber-400">This pay period has been run (submitted).</p>
              <p className="text-muted-foreground">Reopen it on the Payroll Sheet, then come back here to correct the hours.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{fixData.days.length} day(s) with hours or punches</span>
                {canFix && (
                  <Button size="sm" variant="outline" onClick={autoBalanceAll} data-testid="pa-fix-autobalance">
                    <Wand2 className="mr-1 h-4 w-4" /> Auto-balance all days
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {fixData.days.map((d) => {
                  const alloc = dayAllocated(d.date);
                  const bal = Math.abs(alloc - d.clocked_total) <= 0.1;
                  return (
                    <div key={d.date} className="rounded-md border border-border p-3" data-testid={`pa-fix-day-${d.date}`}>
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-semibold">{fmtDShort(d.date)}</span>
                        <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[0.65rem] font-semibold ${bal ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400"}`} data-testid={`pa-fix-daybal-${d.date}`}>
                          allocated {alloc.toFixed(2)} / clocked {d.clocked_total.toFixed(2)} {bal ? "✓" : "⚠"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {fixData.positions.map((pos) => (
                          <div key={pos} className="flex items-center gap-1.5">
                            <Label className="text-xs text-muted-foreground">{pos}{d.rooms?.[pos] ? ` (${d.rooms[pos]} rm)` : ""}</Label>
                            <Input
                              value={fixHours[d.date]?.[pos] ?? ""}
                              onChange={(ev) => setFixHours((h) => ({ ...h, [d.date]: { ...(h[d.date] || {}), [pos]: ev.target.value.replace(/[^0-9.]/g, "") } }))}
                              disabled={!canFix}
                              inputMode="decimal" placeholder="0"
                              className="h-8 w-20 text-right tabular-nums"
                              data-testid={`pa-fix-input-${d.date}-${pos}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {fixData && fixData.status !== "submitted" && canFix && (
            <DialogFooter>
              <Button variant="ghost" onClick={() => setFix(null)}>Cancel</Button>
              <Button onClick={() => saveFix()} disabled={fixSaving} data-testid="pa-fix-save">
                {fixSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save corrections
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
